import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import WrapperPage from "../../components/WrapperPage";
import BaseButton from "../../components/BaseButton";
import useCreateBlankForm from "../../hooks/useCreateBlankForm";
import PersonalAccount from "../../store/PersonalAccount";
import BaseTextButton from "../../components/BaseTextButton";
import useViewController from "./viewController";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import QuestionnaireItems from "./components/QuestionnaireItems";
import BaseSpinner from "../../components/BaseSpinner";
import { INSIGHT_FORM_STATUSES } from "../../utils/constants";
import useCurrentGame from "../../hooks/useCurrentGame";
import Game from "../../store/Game";
import "./style.scss";
import InsightFormProtect from "../../hocs/InsightFormProtect";
import INTOUCH_API from "../../services/Api";
import ROUTES from "../../utils/routes.js";

// const QUESTIONNAIRE_FORM_KEY = "questionnaire-forms";

export default InsightFormProtect(function InsightQuestionnairePage() {
  const { t } = useTranslation();
  const { startBlankForm } = useCreateBlankForm();
  const { getUserData } = useCurrentUserData();
  const { getCurrentGame } = useCurrentGame();
  // 17.09: localStorage может быть пуст (бургер пускал сюда по Game.game из MobX —
  // источники расходятся). Один безопасный геттер на всю страницу вместо
  // getCurrentGame() россыпью, иначе любой из мемо-блоков ниже падает на null.
  const currentGame = () => getCurrentGame() || Game.game;
  const onCreateTopic = () =>
    startBlankForm(currentGame()?.id, PersonalAccount.user?.entitlements);
  const { forms, formsLoading } = useViewController();
  const [insightFormDrafts, setInsightFormDrafts] = useState([]);

  useEffect(() => {
    INTOUCH_API.requestManager("getInsightFormDrafts", {}, (response) => {
      if (response.data) {
        setInsightFormDrafts(response.data);
      }
    });
  }, [forms]);

  const togetherAnsweredQuestionaries = useMemo(
    () =>
      forms.filter((questionarie) =>
        questionarie.every((question) => question.insightFormAnswers.length > 0)
      ),
    [forms]
  );
  const yourDeniedAnsweredQuestionaries = useMemo(() => {
    const currentUser = getUserData();

    return forms.filter((questionarie) => {
      const user = questionarie.find(
        (question) => question.userId === currentUser?.id
      );
      return (
        questionarie.some(
          (question) =>
            question.userId === currentUser?.id &&
            question.status === INSIGHT_FORM_STATUSES.REJECTED
        ) && !user.insightFormAnswers.length
      );
    });
  }, [forms]);

  const partnerDeniedAnsweredQuestionaries = useMemo(() => {
    const game = currentGame();
    const currentUser = getUserData();

    return forms.filter((questionarie) => {
      const user = questionarie.find(
        (question) => question.userId === currentUser?.id
      );

      if (!game?.partner) return false;

      const partner = questionarie.find(
        (question) => question.userId === game.partner.id
      );

      return (
        partner.insightFormAnswers.length === 0 &&
        partner.status === INSIGHT_FORM_STATUSES.REJECTED &&
        (user.status === INSIGHT_FORM_STATUSES.CAN_WATCH ||
          user.status === INSIGHT_FORM_STATUSES.WANT_TO_IMPROVE)
      );
    });
  }, [forms]);

  // первый блок, черновики, анкеты не отвеченные ни одним из партнеров
  const partnerAnsweredQuestionaries = useMemo(() => {
    const game = currentGame();
    const currentUser = getUserData();
    return forms.filter((questionarie) => {
      const user = questionarie.find(
        (question) => question.userId === currentUser?.id
      );

      if (!game?.partner) return false;

      const partner = questionarie.find(
        (question) => question.userId === game.partner.id
      );

      if (
        user.status === INSIGHT_FORM_STATUSES.REJECTED ||
        partner.status === INSIGHT_FORM_STATUSES.REJECTED
      ) {
        return false;
      }
      if (
        partner.insightFormAnswers.length > 0 &&
        user.insightFormAnswers.length > 0
      ) {
        return false;
      }

      return questionarie.some(
        (question) =>
          question.insightFormAnswers.length > 0 &&
          question.userId === game.partner.id &&
          question.status === INSIGHT_FORM_STATUSES.WANT_TO_IMPROVE
      );
    });
  }, [forms]);

  const drafts = useMemo(() => {
    // const formDatas = getItem(QUESTIONNAIRE_FORM_KEY);
    const currentUser = getUserData();
    const game = currentGame();

    return forms.filter((questionarie) => {
      const partner = questionarie.find((question) => {
        if (!game?.partner) return false;
        return question.userId === game.partner.id;
      });

      const draftHasAnswers = insightFormDrafts.filter((element) =>
        element.questions.some(
          (answer) => !!answer.value.length && answer.value !== ":week"
        )
      );

      const user = questionarie.find(
        (question) =>
          (question.userId === currentUser?.id &&
            // BLANK показываем и пустой: она куплена и не пере-выбирается из
            // списка карточек — без этого созданная за баллы анкета «исчезала»,
            // если юзер ушёл, не заполнив ни одного поля
            (question.formType === "BLANK" ||
              question.insightFormAnswers.length)) ||
          draftHasAnswers.some(
            (draft) =>
              draft.userId === currentUser?.id &&
              draft.insightFormId === question.id
          )
      );

      if (partner && partner.status === INSIGHT_FORM_STATUSES.REJECTED) {
        return false;
      }

      // если у партнеров нет ответов, показывать анкету тому кто выбрал карточку и сохранил в черновик
      if (partner) {
        return user && user.isCreated && !partner.insightFormAnswers.length;
      } else {
        return user && user.isCreated;
      }
    });
  }, [forms, insightFormDrafts]);

  return (
    <div className="insight_form_page centering-page">
      <WrapperPage
        headerTitle={t("insight_form_page.title")}
        backBtn
        burgerMenu
      >
        <div className="insight_form_page__read-instruction">
          <BaseTextButton
            to={ROUTES.INSIGHT_INSTRUCTION}
            className="insight_form_page__read-instruction--button"
          >
            {t("insight_form_page.read_instruction")}
          </BaseTextButton>
        </div>
        <div className="insight_form_page__choose-theme">
          <BaseButton unfilled to={ROUTES.INSIGHT_INSIGHT_PICK_CARDS_PAGE}>
            {t("insight_form_page.choose_theme")}
          </BaseButton>
        </div>
        {/* item 17.09: «Создать тему» переехала сюда со страницы выбора карточки.
            Ведёт в тот же флоу чистой анкеты: есть право — сразу форма, нет — попап
            покупки за деньги/баллы, после оплаты форма открывается сама. */}
        <div className="insight_form_page__choose-theme">
          <BaseButton unfilled onClick={onCreateTopic}>
            {t("insight_form_page.create_theme")}
          </BaseButton>
        </div>

        {formsLoading ? (
          <div className="insight_form_page__loader">
            <BaseSpinner />
          </div>
        ) : (
          <>
            {partnerAnsweredQuestionaries.length > 0 && (
              <QuestionnaireItems
                isBlock1
                text={t("insight_form_page.your_partner_want_to_improve")}
                questionaries={partnerAnsweredQuestionaries}
              />
            )}

            {drafts.length > 0 && (
              <QuestionnaireItems
                isBlock2
                text={t("insight_form_page.fillQuestionarieAndSend")}
                questionaries={drafts}
              />
            )}
            {togetherAnsweredQuestionaries.length > 0 && (
              <QuestionnaireItems
                isBlock3
                text={t("insight_form_page.check_out_answers")}
                questionaries={togetherAnsweredQuestionaries}
              />
            )}
            {partnerDeniedAnsweredQuestionaries.length > 0 && (
              <QuestionnaireItems
                isBlock4
                text={t("insight_form_page.partner_not_ready_answer")}
                questionaries={partnerDeniedAnsweredQuestionaries}
              />
            )}
            {yourDeniedAnsweredQuestionaries.length > 0 && (
              <QuestionnaireItems
                isBlock5
                text={t("insight_form_page.your_denied_questionaries")}
                questionaries={yourDeniedAnsweredQuestionaries}
              />
            )}
          </>
        )}
      </WrapperPage>
    </div>
  );
});
